import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { SectionHeading } from '../SectionHeading/SectionHeading'

/**
 * The ProductSearchForm component is meant to be used as
 * an initial search method, that leads into a search results page.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-product-search-form.html
 *
 * @param {ProductSearchFormActionButtons[]|React.ReactNode} [children] - The content to display inside the {@link ProductSearchForm} component.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {string} title - The title of the {@link ProductSearchForm} component.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The {@link ProductSearchForm} component.
 * @constructor
 */
export function ProductSearchForm({ children, className, title, testId, ...otherProps }) {
  const classNameList = classNames('c-product-search-form', className).trim()

  return (
    <article className={classNameList} data-testid={testId} {...otherProps}>
      <SectionHeading center level="2" className="c-product-search-form__heading">
        {title}
      </SectionHeading>
      {children}
    </article>
  )
}

ProductSearchForm.propTypes = {
  children: PropTypes.node, // Bronson template: 'brand-select', 'model-select', 'slider' and 'button'.
  className: PropTypes.string, // Additional CSS classes on the component element.
  title: PropTypes.string.isRequired, // Bronson template: 'title'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

/**
 * The ProductSearchFormActionButtons component is internally used to wrap
 * the action buttons and display it inside the {@link ProductSearchForm}.
 *
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {React.ReactNode} [filterButton] - Button to show more filters.
 * @param {React.ReactNode} resetButton - Button to reset the {@link ProductSearchForm}.
 * @param {React.ReactNode} submitButton - Button to submit the {@link ProductSearchForm}.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {Object} [otherProps] - Other props passed to the component element.
 * @return {JSX.Element} - The {@link ProductSearchFormActionButtons} component.
 * @constructor
 */
function ProductSearchFormActionButtons({ className, filterButton, resetButton, submitButton, testId, ...otherProps }) {
  const classNameList = classNames('c-product-search-form__action-buttons', className).trim()

  return (
    <div className={classNameList} data-testid={testId} {...otherProps}>
      {filterButton}
      <div className="c-product-search-form__button-container">
        {resetButton}
        {submitButton}
      </div>
    </div>
  )
}

ProductSearchFormActionButtons.propTypes = {
  className: PropTypes.string, // Additional CSS classes on the component element.
  filterButton: PropTypes.node, // Bronson template: 'more-button'.
  resetButton: PropTypes.node.isRequired, // Bronson template: 'reset-button'.
  submitButton: PropTypes.node.isRequired, // Bronson template: 'result-button'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}

ProductSearchFormActionButtons.displayName = 'ProductSearchForm.ActionButtons'
ProductSearchForm.ActionButtons = ProductSearchFormActionButtons
